import React, { useEffect, useState, useRef, useCallback } from "react";
import { uploadChunked } from "../lib/upload";
import { authStore } from "../lib/api";
import { toast } from "./ui/sonner";
import { Loader2, Plus, Paperclip, Link2, X, RefreshCw, ChevronDown, ChevronUp, Image as ImageIcon, MapPin, FileText, Layers } from "lucide-react";
import { LayoutDrawEditor } from "./LayoutDrawEditor";
import { LayoutPlansSummary } from "./LayoutPlansSummary";
import ProposalShareButton from "./ProposalShareButton";
import SiteEvalAppointment from "./SiteEvalAppointment";
import { fmtD } from "../lib/date";

const dlUrl = (api, fid) => `${api.defaults.baseURL}/files/${fid}/download?auth=${encodeURIComponent(authStore.get("ali_token") || "")}`;

function ItemAtts({ api, item, zh, onChange, readOnly = false }) {
  const [busy, setBusy] = useState(false);
  const [showLink, setShowLink] = useState(false);
  const [url, setUrl] = useState("");
  const [label, setLabel] = useState("");
  const fileRef = useRef(null);
  const atts = item.attachments || [];
  const links = item.links || [];
  const upload = async (e) => {
    const files = Array.from(e.target.files || []); if (!files.length) return;
    setBusy(true); let ok = 0;
    try {
      for (const f of files) {
        const fin = await uploadChunked(api, f);
        await api.post(`/crm/process/${item.id}/attach`, { attachment: { fileId: fin.fileId, filename: f.name, contentType: f.type } });
        ok += 1;
      }
      toast.success(zh ? `已添加 ${ok} 个附件` : `${ok} attached`); onChange?.();
    } catch (err) { toast.error(err?.response?.data?.detail || (zh ? "上传失败" : "Upload failed")); if (ok) onChange?.(); }
    setBusy(false); if (fileRef.current) fileRef.current.value = "";
  };
  const addLink = async () => {
    if (!url.trim()) return;
    setBusy(true);
    try { await api.post(`/crm/process/${item.id}/attach`, { link: { url: url.trim(), label: label.trim() || url.trim() } }); toast.success(zh ? "链接已添加" : "Link added"); setUrl(""); setLabel(""); setShowLink(false); onChange?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || (zh ? "失败" : "Failed")); }
    setBusy(false);
  };
  const del = async (aid, type) => { try { await api.post(`/crm/process/${item.id}/detach`, { aid, type }); onChange?.(); } catch { toast.error(zh ? "失败" : "Failed"); } };
  return (
    <div className="mt-1 space-y-1.5" data-testid={`dproc-att-${item.id}`}>
      {(atts.length > 0 || links.length > 0) && (
        <div className="flex flex-wrap gap-1.5">
          {atts.map((a) => (a.contentType || "").startsWith("image/") ? (
            <div key={a.aid || a.fileId} className="relative group">
              <a href={dlUrl(api, a.fileId)} target="_blank" rel="noreferrer" title={a.filename}><img src={dlUrl(api, a.fileId)} alt={a.filename} className="h-14 w-14 object-cover rounded-md border border-slate-200 hover:opacity-80" /></a>
              {!readOnly && <button onClick={() => del(a.aid, "file")} data-testid={`dproc-att-del-${a.aid}`} className="absolute -top-1.5 -right-1.5 h-4 w-4 rounded-full bg-red-500 text-white flex items-center justify-center"><X className="h-2.5 w-2.5" /></button>}
            </div>
          ) : (
            <span key={a.aid || a.fileId} className="inline-flex items-center gap-1 rounded-md bg-slate-50 border border-slate-200 pl-2 pr-1 py-1 text-[11px] text-slate-600">
              <a href={dlUrl(api, a.fileId)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:text-indigo-600 max-w-[140px] truncate"><FileText className="h-3 w-3 shrink-0" /><span className="truncate">{a.filename}</span></a>
              {!readOnly && <button onClick={() => del(a.aid, "file")} data-testid={`dproc-att-del-${a.aid}`} className="text-slate-300 hover:text-red-500"><X className="h-3 w-3" /></button>}
            </span>
          ))}
          {links.map((l) => (
            <span key={l.aid} className="inline-flex items-center gap-1 rounded-md bg-violet-50 border border-violet-200 pl-2 pr-1 py-1 text-[11px] text-violet-700">
              <a href={l.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:text-violet-900 max-w-[160px] truncate"><Link2 className="h-3 w-3 shrink-0" /><span className="truncate">{l.label}</span></a>
              {!readOnly && <button onClick={() => del(l.aid, "link")} data-testid={`dproc-link-del-${l.aid}`} className="text-violet-300 hover:text-red-500"><X className="h-3 w-3" /></button>}
            </span>
          ))}
        </div>
      )}
      {!readOnly && <div className="flex items-center gap-2">
        <input type="file" multiple accept="image/*,application/pdf,.doc,.docx,.dwg" ref={fileRef} onChange={upload} className="hidden" data-testid={`dproc-file-input-${item.id}`} />
        <button onClick={() => fileRef.current?.click()} disabled={busy} data-testid={`dproc-add-file-${item.id}`} className="inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-indigo-600 disabled:opacity-50">{busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Paperclip className="h-3 w-3" />}{zh ? "加附件/照片" : "Attach"}</button>
        <button onClick={() => setShowLink((v) => !v)} data-testid={`dproc-add-link-${item.id}`} className="inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-violet-600"><Link2 className="h-3 w-3" />{zh ? "加VR/视频链接" : "Add link"}</button>
      </div>}
      {!readOnly && showLink && (
        <div className="flex flex-wrap items-center gap-1.5">
          <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder={zh ? "粘贴 VR/视频/效果图链接" : "Paste link"} data-testid={`dproc-link-url-${item.id}`} className="text-[11px] rounded-md border border-slate-200 px-2 py-1 flex-1 min-w-[160px]" />
          <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder={zh ? "标题(选填)" : "Label"} className="text-[11px] rounded-md border border-slate-200 px-2 py-1 w-24" />
          <button onClick={addLink} disabled={busy} data-testid={`dproc-link-save-${item.id}`} className="text-[11px] text-white bg-violet-600 hover:bg-violet-700 rounded px-2 py-1">{zh ? "保存" : "Save"}</button>
        </div>
      )}
    </div>
  );
}

function Section({ api, leadId, zh, kind, title, fields, items, onChange, readOnly = false }) {
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(true);
  const add = async () => {
    if (!Object.values(f).some((v) => v !== "" && v != null)) { toast.error(zh ? "请先填写内容" : "Enter details"); return; }
    setBusy(true);
    try { await api.post(`/crm/leads/${leadId}/process`, { kind, ...f }); toast.success(zh ? "已记录" : "Saved"); setF({}); onChange?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || (zh ? "失败" : "Failed")); }
    setBusy(false);
  };
  return (
    <div className="rounded-lg border border-indigo-200 bg-white" data-testid={`dproc-section-${leadId}-${kind}`}>
      <button onClick={() => setOpen((v) => !v)} data-testid={`dproc-toggle-${leadId}-${kind}`} className="w-full flex items-center justify-between px-3 py-2 text-[12px] font-semibold text-indigo-800">
        <span>{title} <span className="text-slate-400">({items.length})</span></span>
        {open ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-2">
          {!readOnly && <div className="flex flex-wrap gap-1.5">
            {fields.map((fl) => (
              <input key={fl.k} type={fl.type || "text"} placeholder={fl.ph} value={f[fl.k] || ""} data-testid={`dproc-${leadId}-${kind}-${fl.k}`}
                onChange={(e) => setF({ ...f, [fl.k]: e.target.value })}
                className="text-[12px] rounded-lg border border-slate-200 px-2 py-1.5 flex-1 min-w-[110px]" />
            ))}
            <button onClick={add} disabled={busy} data-testid={`dproc-add-${leadId}-${kind}`} className="inline-flex items-center gap-1 text-[12px] text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg px-2.5 py-1.5 disabled:opacity-50">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}{zh ? "添加" : "Add"}</button>
          </div>}
          <div className="space-y-2">
            {items.length === 0 && <p className="text-[11px] text-slate-300 text-center py-1">{zh ? "暂无记录" : "None"}</p>}
            {items.map((it) => (
              <div key={it.id} className="border-l-2 border-indigo-100 pl-2 py-0.5" data-testid={`dproc-item-${it.id}`}>
                <div className="text-[11px] text-slate-600">
                  {fmtD(it.createdAt)} · {fields.map((fl) => it[fl.k] ? `${fl.ph}: ${it[fl.k]}` : "").filter(Boolean).join(" · ")}
                  {it.source === "sales" && <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full border font-medium bg-sky-50 border-sky-200 text-sky-700">{zh ? "来自销售" : "Sales"}</span>}
                  {it.ownerConfirmed && <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full border font-medium bg-emerald-50 border-emerald-200 text-emerald-700">{zh ? "业主已确认" : "Owner OK"}</span>}
                  <span className="text-slate-400"> · {it.createdBy || ""}</span>
                </div>
                <ItemAtts api={api} item={it} zh={zh} onChange={onChange} readOnly={readOnly} />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * 销售进程只读镜像（现场评估 / 方案 / 卫星布局）。
 *
 * 2026-10-20 新增 readOnly：
 *   需求背景 —— 签约前营销方案工单详情要在「CRM 客户资料（只读）」块里展示这段内容，
 *   但该详情对设计角色与已签约工单都必须只读。原先本组件把 `canEdit` 硬编码成 true
 *   （见下方 SiteEvalAppointment），详情页只能靠外层包裹层做「视觉提示」，
 *   输入框实际仍可编辑 —— 设计师能在别人的客户里新建评估记录，与需求六「只读」冲突。
 *   最终效果 —— 传 readOnly 时：预约组件、评估 / 方案的新增表单、附件增删、链接增删、
 *   布局绘制入口全部隐藏，只留已存在的记录与附件浏览。
 *   关键取舍 —— 用可选 prop 且默认 false，`DesignConceptBriefs` 等既有调用方行为完全不变，
 *   不改动任何既有页面的可编辑能力。
 */
export default function SalesProcessMirror({ api, leadId, zh, readOnly = false }) {
  const ro = !!readOnly;
  const [data, setData] = useState({ site_eval: [], proposal: [] });
  const [loading, setLoading] = useState(true);
  const [showDraw, setShowDraw] = useState(false);
  const [planTick, setPlanTick] = useState(0);
  const [evalOpts, setEvalOpts] = useState([]);
  useEffect(() => {
    const ROLES = ["design_manager", "us_policy", "eng_manager", "member", "pm"];
    api.get("/users/directory").then((r) => {
      setEvalOpts((r.data || []).filter((u) => u.name && ROLES.includes(u.role))
        .map((u) => { const nm = zh ? (u.nameZh || u.name) : u.name; return { value: nm, label: `${nm}${u.region ? ` · ${u.region.toUpperCase()}` : ""}` }; }));
    }).catch(() => {});
  }, [api, zh]);
  const load = useCallback(async () => {
    setLoading(true);
    try { const { data: d } = await api.get(`/crm/leads/${leadId}/process`); setData(d || {}); } catch { /* */ }
    setLoading(false);
  }, [api, leadId]);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="rounded-lg border border-indigo-200 bg-indigo-50/40 p-3 space-y-2" data-testid={`dproc-${leadId}`}>
      <div className="flex items-center justify-between">
        <p className="text-[12px] font-semibold text-indigo-800 inline-flex items-center gap-1.5"><RefreshCw className="h-3.5 w-3.5" />{zh ? "销售进程（现场评估 / 方案，实时同步销售）" : "Sales Process (Site eval / Proposal)"}</p>
        <button onClick={load} data-testid={`dproc-refresh-${leadId}`} className="text-slate-400 hover:text-indigo-600"><RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /></button>
      </div>
      <div className="rounded-lg border border-red-200 bg-white p-2.5 space-y-2" data-testid={`dproc-layout-${leadId}`}>
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <span className="text-[12px] font-semibold text-red-700 inline-flex items-center gap-1"><Layers className="h-3.5 w-3.5" />{zh ? "卫星地图布局绘制 · ADU 方案" : "Satellite Layout · ADU Plan"}</span>
          <div className="flex items-center gap-1.5">
            <ProposalShareButton api={api} leadId={leadId} zh={zh} />
            {/* 只读态不给「绘制」入口：它会写新的布局方案记录，属于销售侧动作 */}
            {!ro && <button onClick={() => setShowDraw(true)} data-testid={`dproc-open-draw-${leadId}`} className="inline-flex items-center gap-1 text-[12px] text-white bg-brand-500 hover:bg-brand-600 rounded-lg px-2.5 py-1.5"><MapPin className="h-3.5 w-3.5" />{zh ? "打开绘制（截图+绘制+保存方案）" : "Open layout designer"}</button>}
          </div>
        </div>
        <LayoutPlansSummary api={api} zh={zh} leadId={leadId} refreshKey={planTick} />
      </div>
      <SiteEvalAppointment api={api} zh={zh} leadId={leadId} evaluatorOptions={evalOpts} canEdit={!ro} />
      <Section api={api} leadId={leadId} zh={zh} kind="site_eval" title={<span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{zh ? "预约现场评估" : "Schedule Site Evaluation"}</span>}
        fields={[{ k: "scheduledDate", ph: zh ? "预约日期" : "Date", type: "date" }, { k: "feasibility", ph: zh ? "具体时间" : "Time" }, { k: "notes", ph: zh ? "现场评估人员/备注" : "Evaluator/notes" }]}
        items={data.site_eval || []} onChange={load} readOnly={ro} />
      <Section api={api} leadId={leadId} zh={zh} kind="proposal" title={<span className="inline-flex items-center gap-1"><ImageIcon className="h-3.5 w-3.5" />{zh ? "方案 Proposal" : "Proposal"}</span>}
        fields={[{ k: "title", ph: zh ? "方案标题" : "Title" }, { k: "notes", ph: zh ? "方案说明" : "Notes" }]}
        items={data.proposal || []} onChange={load} readOnly={ro} />
      <LayoutDrawEditor open={showDraw} onClose={() => { setShowDraw(false); setPlanTick((t) => t + 1); }} api={api} zh={zh} leadId={leadId} onSaved={() => setPlanTick((t) => t + 1)} />
    </div>
  );
}
